<template>
  <div>
    <ToastificationLoadingContent ref="loadingToast" />
    <ToastificationProgressContent ref="progressToast" />

    <Sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
    />

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters :tenant-filter.sync="tenantFilter" :tenant-options="tenantOptions" />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="mx-2 mt-1" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <PerPageSelector :perPage.sync="perPage" :perPageOptions="perPageOptions" />
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'AiAnalyticsServers')"
              >
                <span class="text-nowrap text-capitalize">{{
                  `${$t("actions.add")} ${$tc("ai_analytics_servers.server", 1)}`
                }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                v-if="$can('update', 'AiAnalyticsServers')"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('delete', 'AiAnalyticsServers')"
                class="border-0"
                button
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                class="border-0"
                :to="`/ai_analytics_servers/${data.item.id}/devices?server_type=${data.item.server_type || ''}`"
              >
                <feather-icon icon="HardDriveIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("ai_analytics_servers.devices") }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>

        <template #cell(online)="data">
          <b-badge :variant="data.item.online ? 'success' : 'danger'">
            {{ data.item.online ? "Online" : "Offline" }}
          </b-badge>
        </template>
      </b-table>

      <!-- Footer Table -->
      <TableFooter
        :dataMeta="dataMeta"
        :totalList="totalList"
        :currentPage="currentPage"
        :perPage="perPage"
        @updatePagination="updatePagination"
      />
    </b-card>
  </div>
</template>

<script>
import { ref } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationProgressContent from "@core/components/toastification/ToastificationProgressContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import TableFooter from "@/components/TableFooter.vue";
import PerPageSelector from "@/components/PerPageSelector.vue";
import Filters from "./Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";

export default {
  name: "AiAnalyticsServers",
  components: {
    Sidenav,
    BreadcrumbMenu,
    TableFooter,
    PerPageSelector,
    ToastificationLoadingContent,
    ToastificationProgressContent,
    Filters,
    FilterModal,
    ButtonFilters,
  },
  setup() {
    const loadingToast = ref(null);
    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
    };

    const { t } = useI18nUtils();
    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
    };

    const {
      tableColumns,
      perPage,
      currentPage,
      totalList,
      fetchList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      updatePagination,
      tenantOptions,
      tenantFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("ai_analytics_servers/servers/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 5000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response?.data?.message || "Error al eliminar",
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 5000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      fetchList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      updatePagination,
      tenantOptions,
      tenantFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
    };
  },
};
</script>
